class ResourceCoordinatorBuilder {
  #cached = new Set();
  #resources = new Map();
  #loadingPromises = new Map();

  registerVendor(vendorId, { urls = [], version = "1", postFetch = null }) {
    this.#resources.set(vendorId, { urls, version, postFetch });
  }

  unregisterVendor(vendorId) {
    this.#resources.delete(vendorId);
    this.#cached.delete(vendorId);
  }

  async requestVendor(vendorId) {
    if (!vendorId) throw new Error("Please enter resource vendorId");

    const cacheKey = `${vendorId}_${this.#resources.get(vendorId)?.version || ""}`;
    if (this.#cached.has(cacheKey)) return;

    if (this.#loadingPromises.has(cacheKey)) {
      return this.#loadingPromises.get(cacheKey);
    }

    const loadPromise = this.#loadVendorResources(vendorId, cacheKey);
    this.#loadingPromises.set(cacheKey, loadPromise);

    try {
      await loadPromise;
    } finally {
      this.#loadingPromises.delete(cacheKey);
    }
  }

  async #loadVendorResources(vendorId, cacheKey) {
    const resource = this.#resources.get(vendorId);
    if (!resource) throw new Error(`Cannot find resource for vendor: ${vendorId}`);

    const { urls, postFetch } = resource;
    const [stylesheets, scripts] = this.#partition(urls, (url) => new URL(url, location.origin).pathname.endsWith(".css"));
    try {
      await Promise.all([this.#loadStylesheets(stylesheets), this.#loadScripts(scripts)]);

      if (postFetch) await postFetch();

      this.#cached.add(cacheKey);
    } catch (error) {
      console.error(`Failed to load resources for vendor ${vendorId}:`, error);
      throw error;
    }
  }

  #partition(collection, predicate) {
    return collection.reduce(
      (result, item) => {
        result[predicate(item) ? 0 : 1].push(item);
        return result;
      },
      [[], []],
    );
  }

  async #loadStylesheets(urls) {
    return Promise.all(urls.map((url) => this.#loadStylesheet(url)));
  }

  #loadStylesheet(href) {
    return new Promise((resolve, reject) => {

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.type = "text/css";
      link.href = href;
      link.media = "print";

      link.onload = () => {
        link.media = "all";
        resolve(link);
      };
      link.onerror = reject;

      const vendorCustomStyles = document.getElementById("vendor-custom-styles");
      if (vendorCustomStyles) {
        vendorCustomStyles.parentNode.insertBefore(link, vendorCustomStyles);
      } else {
        document.head.appendChild(link);
      }
    });
  }

  async #loadScripts(urls) {
    for (const url of urls) {
      await this.#loadScript(url);
    }
  }

  #loadScript(url) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  clearCache() {
    this.#cached.clear();
  }
}

window.ResourceCoordinator = new ResourceCoordinatorBuilder();

    
